import { useEffect, useRef, useState } from 'react';

/**
 * The stage behind every console screen: a blurred, darkened version of the
 * game in focus. Screens set a base image; focusing a box art previews its game.
 */
type Listener = (url: string | null) => void;
let base: string | null = null;
let preview: string | null = null;
const listeners = new Set<Listener>();
const current = () => preview ?? base;
const emit = () => listeners.forEach((l) => l(current()));

export function setAmbientBase(url: string | null) {
  base = url;
  preview = null;
  emit();
}
let previewTimer: ReturnType<typeof setTimeout> | undefined;
/** Debounced so fast scrolling through a rail doesn't thrash the background. */
export function previewAmbient(url: string | null) {
  clearTimeout(previewTimer);
  previewTimer = setTimeout(() => {
    if (url === preview) return;
    preview = url;
    emit();
  }, 160);
}

/** Set the screen's default background while mounted. */
export function useAmbient(url: string | null | undefined) {
  useEffect(() => {
    setAmbientBase(url ?? null);
    return () => setAmbientBase(null);
  }, [url]);
}

/** Image art, or `color:#hex` for games without art (a soft glow in the console's colour). */
function layerStyle(v: string): React.CSSProperties {
  if (v.startsWith('color:')) {
    const c = v.slice(6);
    return { backgroundImage: `radial-gradient(55% 60% at 72% 28%, ${c} 0%, transparent 70%), radial-gradient(40% 50% at 20% 10%, ${c}55 0%, transparent 70%)`, filter: 'blur(40px) brightness(0.75)' };
  }
  return { backgroundImage: `url("${v}")` };
}

/** Ambient value for a game: its art if any, else its console colour. */
export function ambientFor(g: { coverUrl: string | null; heroUrl?: string | null; systemId: string }, accent: (id: string) => string) {
  return g.heroUrl ?? g.coverUrl ?? `color:${accent(g.systemId)}`;
}

export function Ambient() {
  const [state, setState] = useState<{ layers: [string | null, string | null]; front: number }>({ layers: [null, null], front: 0 });
  const ref = useRef(state);
  ref.current = state;
  useEffect(() => {
    const l: Listener = (url) => {
      const cur = ref.current;
      if (cur.layers[cur.front] === url) return;
      const back = cur.front ^ 1;
      const layers: [string | null, string | null] = [...cur.layers];
      layers[back] = url;
      setState({ layers, front: back });
    };
    listeners.add(l);
    return () => void listeners.delete(l);
  }, []);
  const { layers, front } = state;
  const active = layers[front];
  return (
    <div className={`ambient${active ? '' : ' idle'}`} aria-hidden="true">
      {layers.map((url, i) => (
        <i key={i} className={i === front && url ? 'on' : ''} style={url ? layerStyle(url) : undefined} />
      ))}
    </div>
  );
}
